import React from 'react'
import { Config, Data, DefaultRootFieldProps } from '@puckeditor/core'
import {
  POLL_FIELDS,
  POLL_INITIAL_DATA,
  PollContainerProps,
  PuckPollContainer,
} from './components/PollContainer/PollContainer'
import {
  PuckPollQuestion,
  PollQuestionProps,
} from './components/PollQuestion/PollQuestion'
import {
  PuckPollAnswer,
  PollAnswerProps,
} from './components/PollAnswer/PollAnswer'
import {
  PuckPollProductPanel,
  PollProductPanelProps,
} from './components/PollProductPanel/PollProductPanel'
import {
  PuckPollProduct,
  PollProductProps,
} from './components/PollProduct/PollProduct'
import {
  PuckPollNextQuestionButton,
  PollNextQuestionButtonProps,
} from './components/PollNextQuestionButton/PollNextQuestionButton'
import {
  PuckPollDropdownButton,
  PollDropdownButtonProps,
} from './components/PollDropdownButton/PollDropdownButton'
import {
  PuckPollSkipButton,
  PollSkipButtonProps,
} from './components/PollSkipButton/PollSkipButton'
import {
  PuckPollProgressBar,
  PollProgressBarProps,
} from './components/PollProgressBar/PollProgressBar'
import {
  PuckPollSocialShare,
  PollSocialShareProps,
} from './components/PollSocialShare/PollSocialShare'
import {
  PuckPollStartScreen,
  PollStartScreenProps,
  PuckPollStartScreenCard,
  PollStartScreenCardProps,
  PuckPollStartScreenButton,
  PollStartScreenButtonProps,
} from './components/PollStartScreen/PollStartScreen'
import {
  LISTICLE_FIELDS,
  LISTICLE_INITIAL_DATA,
  ListicleContainerProps,
  PuckListicleContainer,
} from './components/ListicleContainer/ListicleContainer'
import {
  PuckListicleItem,
  ListicleItemProps,
} from './components/ListicleItem/ListicleItem'
import {
  PuckListicleImageComparison,
  ListicleImageComparisonProps,
} from './components/ListicleImageComparison/ListicleImageComparison'
import {
  PuckListicleMenu,
  ListicleMenuProps,
} from './components/ListicleMenu/ListicleMenu'
import {
  PuckListicleVoting,
  ListicleVotingProps,
} from './components/ListicleVoting/ListicleVoting'
import {
  REFERRAL_FIELDS,
  REFERRAL_INITIAL_DATA,
  ReferralContainerProps,
  PuckReferralContainer,
} from './components/ReferralContainer/ReferralContainer'
import {
  PuckReferralCard,
  ReferralCardProps,
} from './components/ReferralCard/ReferralCard'
import {
  PuckReferralButton,
  ReferralButtonProps,
} from './components/ReferralButton/ReferralButton'
import {
  PuckReferralDropdown,
  ReferralDropdownProps,
} from './components/ReferralDropdown/ReferralDropdown'
import {
  PQ_FIELDS,
  PQ_INITIAL_DATA,
  PQContainerProps,
  PuckPQContainer,
} from './components/PQContainer/PQContainer'
import {
  PuckPQQuestion,
  PQQuestionProps,
} from './components/PQQuestion/PQQuestion'
import { PuckPQAnswer, PQAnswerProps } from './components/PQAnswer/PQAnswer'
import { PuckPQResult, PQResultProps } from './components/PQResult/PQResult'
import {
  THEME_CATEGORY_NAMES,
  ThemeComponents,
  ThemeCustomFields,
  createPuckConfig,
  defineComponent,
} from 'breaktime-theme/src/puck/theme-config'
import {
  offersSectionConfig,
  OffersSectionProps,
} from 'breaktime-theme/src/steps/promo-page/OffersSectionBlock'

type Components = ThemeComponents & {
  PollContainer: PollContainerProps
  PollQuestion: PollQuestionProps
  PollAnswer: PollAnswerProps
  PollProductPanel: PollProductPanelProps
  PollProduct: PollProductProps
  PollNextQuestionButton: PollNextQuestionButtonProps
  PollDropdownButton: PollDropdownButtonProps
  PollSkipButton: PollSkipButtonProps
  PollProgressBar: PollProgressBarProps
  PollSocialShare: PollSocialShareProps
  /** Canonical shared share block; PollSocialShare is the hidden legacy
   * alias kept so existing saved steps keep rendering. */
  SocialShare: PollSocialShareProps
  PollStartScreen: PollStartScreenProps
  PollStartScreenCard: PollStartScreenCardProps
  PollStartScreenButton: PollStartScreenButtonProps
  ListicleContainer: ListicleContainerProps
  ListicleItem: ListicleItemProps
  ListicleImageComparison: ListicleImageComparisonProps
  ListicleMenu: ListicleMenuProps
  ListicleVoting: ListicleVotingProps
  OffersSection: OffersSectionProps
  ReferralContainer: ReferralContainerProps
  ReferralCard: ReferralCardProps
  /** Canonical shop/CTA button; ReferralButton is the hidden legacy alias. */
  ShopButton: ReferralButtonProps
  ReferralButton: ReferralButtonProps
  ReferralDropdown: ReferralDropdownProps
  PQContainer: PQContainerProps
  PQQuestion: PQQuestionProps
  PQAnswer: PQAnswerProps
  PQResult: PQResultProps
}

type RootProps = {
  breakType: 'editorial' | 'sponsored'
  stepLabel: string
}

export type CustomFields = ThemeCustomFields

export type PuckData = Data<Components, RootProps & DefaultRootFieldProps>
export type PuckConfig = Config<{
  components: Components
  root: RootProps
  categories: [...typeof THEME_CATEGORY_NAMES, 'poll', 'listicle', 'referral', 'personalityQuiz', 'steps', 'hidden']
  fields: CustomFields
}>

export const config = createPuckConfig<PuckConfig>({
  components: {
    PollContainer: defineComponent<PollContainerProps>({
      label: 'Poll',
      defaultProps: POLL_INITIAL_DATA,
      fields: POLL_FIELDS,
      resolveFields: (_data, { appState }) => {
        const isEditorial = (appState as any).data?.root?.props?.breakType === 'editorial'
        if (isEditorial) {
          const { background: _bg, ...rest } = POLL_FIELDS
          return rest
        }
        return POLL_FIELDS
      },
      render: props => <PuckPollContainer {...props} />,
    }),
    PollQuestion: PuckPollQuestion,
    PollAnswer: PuckPollAnswer,
    PollProductPanel: PuckPollProductPanel,
    PollProduct: PuckPollProduct,
    PollNextQuestionButton: PuckPollNextQuestionButton,
    PollDropdownButton: PuckPollDropdownButton,
    PollSkipButton: PuckPollSkipButton,
    PollProgressBar: PuckPollProgressBar,
    PollSocialShare: PuckPollSocialShare,
    SocialShare: PuckPollSocialShare,
    PollStartScreen: defineComponent<PollStartScreenProps>({
      ...PuckPollStartScreen,
      resolveFields: (_data, { appState }) => {
        const isEditorial = (appState as any).data?.root?.props?.breakType === 'editorial'
        if (isEditorial) {
          const { background: _bg, ...rest } = PuckPollStartScreen.fields!
          return rest
        }
        return PuckPollStartScreen.fields!
      },
    }),
    PollStartScreenCard: PuckPollStartScreenCard,
    PollStartScreenButton: PuckPollStartScreenButton,
    ListicleContainer: defineComponent<ListicleContainerProps>({
      label: 'Listicle',
      defaultProps: LISTICLE_INITIAL_DATA,
      fields: LISTICLE_FIELDS,
      resolveFields: (_data, { appState }) => {
        const isEditorial =
          (appState as any).data?.root?.props?.breakType === 'editorial'
        if (isEditorial) {
          const { background: _bg, ...rest } = LISTICLE_FIELDS
          return rest
        }
        return LISTICLE_FIELDS
      },
      render: props => <PuckListicleContainer {...props} />,
    }),
    ListicleItem: PuckListicleItem,
    ListicleImageComparison: PuckListicleImageComparison,
    ListicleMenu: PuckListicleMenu,
    ListicleVoting: PuckListicleVoting,
    OffersSection: offersSectionConfig,
    ReferralContainer: defineComponent<ReferralContainerProps>({
      label: 'Referral',
      defaultProps: REFERRAL_INITIAL_DATA,
      fields: REFERRAL_FIELDS,
      resolveFields: (_data, { appState }) => {
        const isEditorial =
          (appState as any).data?.root?.props?.breakType === 'editorial'
        if (isEditorial) {
          const { background: _bg, ...rest } = REFERRAL_FIELDS
          return rest
        }
        return REFERRAL_FIELDS
      },
      render: props => <PuckReferralContainer {...props} />,
    }),
    ReferralCard: PuckReferralCard,
    ShopButton: PuckReferralButton,
    ReferralButton: PuckReferralButton,
    ReferralDropdown: PuckReferralDropdown,
    PQContainer: defineComponent<PQContainerProps>({
      label: 'Personality Quiz',
      defaultProps: PQ_INITIAL_DATA,
      fields: PQ_FIELDS,
      resolveFields: (_data, { appState }) => {
        const isEditorial =
          (appState as any).data?.root?.props?.breakType === 'editorial'
        if (isEditorial) {
          const { background: _bg, ...rest } = PQ_FIELDS
          return rest
        }
        return PQ_FIELDS
      },
      render: props => <PuckPQContainer {...props} />,
    }),
    PQQuestion: PuckPQQuestion,
    PQAnswer: PuckPQAnswer,
    PQResult: PuckPQResult,
  },
  root: {
    fields: {
      breakType: {
        type: 'radio',
        label: 'Break Type',
        options: [
          { label: 'Editorial', value: 'editorial' },
          { label: 'Sponsored', value: 'sponsored' },
        ],
      },
      stepLabel: {
        type: 'text',
        label: 'Step Label',
        visible: false,
      },
    },
    defaultProps: {
      stepLabel: 'Poll',
      breakType: 'editorial',
    },
    render: ({ children }) => <>{children}</>,
  },
  categories: {
    hidden: {
      visible: false,
      components: [
        'PollNextQuestionButton',
        'PollStartScreenCard',
        'PollStartScreenButton',
        'PollProgressBar',
        // legacy aliases kept registered for old saved steps
        'PollSocialShare',
        'ReferralButton',
      ],
    },
    poll: {
      title: 'Poll',
      components: ['PollContainer'],
      subcategories: {
        questions: {
          title: 'Questions & Answers',
          components: ['PollQuestion', 'PollAnswer'],
        },
        products: {
          title: 'Products',
          components: ['PollProductPanel', 'PollProduct'],
        },
        actions: {
          title: 'Actions',
          components: ['PollDropdownButton', 'PollSkipButton'],
        },
      },
    },
    listicle: {
      title: 'Listicle',
      components: [
        'ListicleContainer',
        'ListicleItem',
        'ListicleImageComparison',
        'ListicleMenu',
        'ListicleVoting',
      ],
    },
    referral: {
      title: 'Referral',
      components: [
        'ReferralContainer',
        'ReferralCard',
        'ReferralDropdown',
      ],
    },
    personalityQuiz: {
      title: 'Personality Quiz',
      components: ['PQContainer', 'PQQuestion', 'PQAnswer', 'PQResult'],
    },
    // Shared cross-step blocks (start screen, social share, …)
    steps: {
      title: 'Steps',
      components: [
        'PollStartScreen',
        'SocialShare',
        'ShopButton',
        'OffersSection',
      ],
    },
  },
})
